import { nanoid } from '@reduxjs/toolkit'
import { FC } from 'react'
import { StyleSheet } from 'react-native'
import { List } from 'react-native-paper'
import { ColorsEnum } from '../../constants/colors'
import { getProductColor } from '../../helpers/getProductColor'
import { useAppSelector } from '../../hooks/redux'
import { useProductQuantityChange } from '../../hooks/useProductQuantityChange'
import { appSelectors } from '../../redux/reducers/app'
import { IProduct } from '../../types/product'
import ItemWithQuantity from '../UI/ItemWithQuantity/ItemWithQuantity'
import IngredientItem from '../UI/ProductItem/IngredientItem'
import ProductItem from '../UI/ProductItem/ProductItem'

const Product: FC<IProduct> = ({
    orderKey,
    id,
    name,
    isPreparable,
    readyQuantity,
    totalQuantity,
    addedIngredients,
    removedIngredients,
}) => {
    const isArchive = useAppSelector(appSelectors.isArchive)

    const [localReadyQuantity, handleQuantityChange, isLoading] = useProductQuantityChange(
        orderKey,
        id,
        readyQuantity
    )

    const color = getProductColor(localReadyQuantity, totalQuantity, isLoading)

    const withQuantityChange = isPreparable && !isArchive

    return (
        <ProductItem style={!withQuantityChange && styles.withoutQuantityChange}>
            <ItemWithQuantity
                readyQuantity={localReadyQuantity}
                totalQuantity={totalQuantity}
                color={color}
                withQuantityChange={withQuantityChange}
                isLoading={isLoading}
                onQuantityDecrease={() => handleQuantityChange(-1)}
                onQuantityIncrease={() => handleQuantityChange(1)}
            >
                {name}
            </ItemWithQuantity>
            <List.Section style={styles.ingredientsContainer}>
                {addedIngredients?.map(({ name, quantity }, index) => (
                    <IngredientItem
                        key={nanoid()}
                        color={ColorsEnum.GRAY_DARK}
                        style={[
                            index === 0 && styles.firstIngredient,
                            index === addedIngredients!.length - 1 &&
                                !removedIngredients?.length &&
                                styles.lastIngredient,
                        ]}
                    >{`+${quantity > 1 ? quantity : ''} ${name}`}</IngredientItem>
                ))}
                {removedIngredients?.map(({ name }, index) => (
                    <IngredientItem
                        key={nanoid()}
                        color={ColorsEnum.PRIMARY}
                        style={[
                            index === removedIngredients!.length - 1 && styles.lastIngredient,
                            index === 0 && !addedIngredients?.length && styles.firstIngredient,
                        ]}
                    >{`- ${name}`}</IngredientItem>
                ))}
            </List.Section>
        </ProductItem>
    )
}

const styles = StyleSheet.create({
    withoutQuantityChange: {
        paddingVertical: 15,
    },
    ingredientsContainer: {
        marginVertical: 0,
    },
    firstIngredient: {
        paddingTop: 0,
    },
    lastIngredient: {
        paddingBottom: 0,
    },
})

export default Product
